import { eventBus } from './core/EventBus';
import { GameLoop } from './core/GameLoop';
import { SaveManager } from './core/SaveManager';
import { GameState, createDefaultState } from './core/GameState';
import { bn } from './core/BigNumber';
import { BuildingSystem } from './systems/BuildingSystem';
import { CpsCalculator } from './systems/CpsCalculator';
import { ClickSystem } from './systems/ClickSystem';
import { UpgradeSystem } from './systems/UpgradeSystem';
import { AchievementSystem } from './systems/AchievementSystem';
import { BuffSystem } from './systems/BuffSystem';
import { ShimmerSystem } from './systems/ShimmerSystem';
import { StoreRenderer } from './ui/StoreRenderer';
import { ParticleSystem } from './ui/ParticleSystem';
import { NotificationSystem } from './ui/NotificationSystem';
import { TooltipSystem } from './ui/TooltipSystem';
import { PromptSystem } from './ui/PromptSystem';
import { MenuSystem } from './ui/MenuSystem';
import { PlayFunIntegration } from './integrations/PlayFunIntegration';
import { BUILDINGS } from './data/buildings';
import { BUFF_TYPES } from './data/buffs';
import { beautify } from './utils/format';
import { $id } from './utils/dom';
import { buildingPrice, buildingBulkPrice } from './utils/math';
import './styles/main.css';

let state: GameState;

const loop = new GameLoop();
const saveManager = new SaveManager();
const buildingSystem = new BuildingSystem();
const upgradeSystem = new UpgradeSystem();
const achievementSystem = new AchievementSystem();
const cpsCalc = new CpsCalculator(upgradeSystem, achievementSystem);
const clickSystem = new ClickSystem(cpsCalc);
const buffSystem = new BuffSystem();
const shimmerSystem = new ShimmerSystem();
const particleSystem = new ParticleSystem();
const notificationSystem = new NotificationSystem();
const tooltipSystem = new TooltipSystem();
const promptSystem = new PromptSystem();
const playFunIntegration = new PlayFunIntegration();
let menuSystem: MenuSystem;
let storeRenderer: StoreRenderer;
const PLAYFUN_SUBMIT_INTERVAL_MS = 60_000;
const PLAYFUN_MAX_COOKIES_DELTA = 1e15;
const PLAYFUN_MAX_CPS_INPUT = 1e12;
let lastPlayFunCookiesEarned = bn(0);
let playFunSubmitTimerMs = 0;
let playFunSubmitInFlight = false;
let lastPlayFunSubmitResult: unknown = null;

function getState(): GameState { return state; }

async function init(): Promise<void> {
  await playFunIntegration.init();

  let saved: GameState | null = null;
  if (playFunIntegration.isEnabled() && playFunIntegration.isReady()) {
    const serverSave = await Promise.race([
      playFunIntegration.loadSaveState(),
      new Promise<string | null>((r) => setTimeout(() => r(null), 5000)),
    ]);
    if (serverSave) saved = saveManager.importSave(serverSave);
  }
  if (!saved) saved = saveManager.load();
  state = saved ?? createDefaultState();
  if (saved) saveManager.save(state); // Sync to localStorage (backup when loaded from server)

  buildingSystem.init(state);
  upgradeSystem.init(state);
  cpsCalc.recalculate(state);

  setupUI();

  shimmerSystem.init();
  particleSystem.init('backgroundCanvas');
  notificationSystem.init('notifications');
  tooltipSystem.init('tooltip');
  promptSystem.init('prompt-overlay', 'prompt-box');

  storeRenderer = new StoreRenderer('upgrades', upgradeSystem, tooltipSystem, getState, (id) => {
    upgradeSystem.buy(state, id);
  });

  menuSystem = new MenuSystem(saveManager, promptSystem, tooltipSystem, getState, {
    onSaveToCloud: playFunIntegration.isEnabled()
      ? async () => {
          if (!playFunIntegration.isReady()) return false;
          const encoded = saveManager.exportSave(state);
          return playFunIntegration.saveSaveState(encoded);
        }
      : undefined,
    showToast: (msg) => notificationSystem.show(msg),
  });
  menuSystem.init('menuContent');

  saveManager.startAutosave(() => state);

  loop.onLogic((dt) => logicTick(dt));
  loop.onRender((dt) => renderTick(dt));
  loop.start();

  wireEvents();
  applyOfflineProgress();
}

function wireEvents(): void {
  eventBus.on('gains:recalculate', () => cpsCalc.recalculate(state));
  eventBus.on('achievement:won', (_id: string, def: { name: string }) => {
    notificationSystem.showAchievement(def.name);
  });
  eventBus.on('buff:gained', (buff: { name: string }) => {
    notificationSystem.showBuff(buff.name);
  });
  eventBus.on('golden:frenzy', () => {
    buffSystem.gain(state, 'frenzy', 77);
  });
  eventBus.on('golden:click_frenzy', () => {
    buffSystem.gain(state, 'click_frenzy', 13);
  });
  eventBus.on('golden:clot', () => {
    buffSystem.gain(state, 'clot', 66);
  });
  eventBus.on('golden:lucky', () => {
    const bonus = state.cookiesPs.mul(900);
    state.cookies = state.cookies.add(bonus);
    state.cookiesEarned = state.cookiesEarned.add(bonus);
    notificationSystem.show(`Lucky! +${beautify(bonus)} cookies!`);
  });
}

function applyOfflineProgress(): void {
  const offlineMs = Date.now() - state.lastSaveDate;
  if (offlineMs > 60_000 && state.cookiesPs.gt(0)) {
    const earned = state.cookiesPs.mul(offlineMs / 1000);
    state.cookies = state.cookies.add(earned);
    state.cookiesEarned = state.cookiesEarned.add(earned);
    notificationSystem.show(`Welcome back! Earned ${beautify(earned)} cookies while away.`);
    // Do not convert offline grant directly into Play.fun points on next submit.
    lastPlayFunCookiesEarned = state.cookiesEarned;
  }
}

let checkTimer = 0;

function logicTick(dt: number): void {
  state.tickCount++;

  if (state.cookiesPs.gt(0)) {
    const gain = state.cookiesPs.div(30);
    state.cookies = state.cookies.add(gain);
    state.cookiesEarned = state.cookiesEarned.add(gain);
  }

  buffSystem.update(state, dt);
  shimmerSystem.update(state, dt);

  checkTimer++;
  if (checkTimer >= 30) {
    checkTimer = 0;
    upgradeSystem.checkUnlocks(state);
    achievementSystem.check(state);
  }

  playFunSubmitTimerMs += dt;
  if (playFunSubmitTimerMs >= PLAYFUN_SUBMIT_INTERVAL_MS) {
    playFunSubmitTimerMs = 0;
    void submitPlayFunEndRound();
  }
}

function renderTick(dt: number): void {
  $id('cookies').textContent = beautify(state.cookies);
  $id('cps').textContent = beautify(state.cookiesPs);

  let lockedShown = 0;
  const MAX_LOCKED_VISIBLE = 2;

  for (const def of BUILDINGS) {
    const bs = state.buildings.get(def.id);
    if (!bs) continue;
    const btn = document.getElementById(`building-${def.id}`);
    if (!btn) continue;

    const unlocked = def.isUnlocked(state);
    const bulk = state.bulkBuyAmount;
    const price = bulk > 1
      ? buildingBulkPrice(bn(def.basePrice), def.priceMultiplier, bs.amount, bulk)
      : buildingPrice(bn(def.basePrice), def.priceMultiplier, bs.amount);
    const canAfford = state.cookies.gte(price);

    const iconEl = btn.querySelector('.building-icon');
    const nameEl = btn.querySelector('.building-name');
    const priceEl = btn.querySelector('.building-price');
    const amtEl = btn.querySelector('.building-amount');

    if (unlocked) {
      btn.classList.remove('locked', 'locked-hidden');
      btn.classList.toggle('affordable', canAfford);
      if (iconEl) iconEl.textContent = def.icon;
      if (nameEl) nameEl.textContent = def.name;
      if (priceEl) priceEl.textContent = beautify(price);
      if (amtEl) amtEl.textContent = String(bs.amount);
    } else {
      lockedShown++;
      btn.classList.remove('affordable');
      btn.classList.toggle('locked', lockedShown <= MAX_LOCKED_VISIBLE);
      btn.classList.toggle('locked-hidden', lockedShown > MAX_LOCKED_VISIBLE);
      if (iconEl) iconEl.textContent = '❓';
      if (nameEl) nameEl.textContent = '???';
      if (priceEl) priceEl.textContent = '???';
      if (amtEl) amtEl.textContent = '';
    }
  }

  storeRenderer.render(state);
  storeRenderer.renderOwned(state);
  particleSystem.update(dt);
  renderBuffBar();
}

let buffBarSignature = '';

function renderBuffBar(): void {
  const container = document.getElementById('buff-bar');
  if (!container) return;

  const buffs = Array.from(state.buffs.values());
  const signature = buffs.map((buff) => buff.typeId).join('|');

  if (signature !== buffBarSignature) {
    buffBarSignature = signature;
    container.innerHTML = buffs.map((buff) => `
      <div class="buff-icon" data-buff-id="${buff.typeId}">
        <span class="buff-emoji">${buffSystem.getTypeDef(buff.typeId)?.icon ?? '✨'}</span>
        <div class="buff-timer"></div>
      </div>
    `).join('');

    container.querySelectorAll<HTMLElement>('.buff-icon').forEach((iconEl) => {
      tooltipSystem.attach(iconEl, () => {
        const typeId = iconEl.dataset.buffId;
        if (!typeId) return '';
        const liveBuff = state.buffs.get(typeId);
        if (!liveBuff) return '';
        const def = buffSystem.getTypeDef(typeId);
        const remaining = Math.ceil(liveBuff.time);
        return `
          <div style="min-width:220px">
            <div style="font-size:16px;font-weight:700;margin-bottom:4px">
              ${def?.icon ?? '✨'} ${liveBuff.name}
            </div>
            <div style="font-size:13px;color:#d6c1a6;margin-bottom:8px">
              ${def?.desc ?? 'Temporary buff effect.'}
            </div>
            <div style="font-size:13px;color:var(--gold)">
              Remaining: <strong>${remaining}s</strong>
            </div>
          </div>
        `;
      });
    });
  }

  for (const buff of buffs) {
    const iconEl = container.querySelector<HTMLElement>(`.buff-icon[data-buff-id="${buff.typeId}"]`);
    if (!iconEl) continue;

    const timerEl = iconEl.querySelector<HTMLElement>('.buff-timer');
    const pct = Math.max(0, Math.min(100, (buff.time / buff.maxTime) * 100));
    if (timerEl) timerEl.style.width = `${pct}%`;

    const def = buffSystem.getTypeDef(buff.typeId);
    const tooltipText = `${buff.name} (${Math.ceil(buff.time)}s)\n${def?.desc ?? ''}`.trim();
    iconEl.title = tooltipText;
    iconEl.setAttribute('aria-label', tooltipText);
  }
}

function setupUI(): void {
  $id('bigCookie').addEventListener('click', (e) => onCookieClick(e));

  const store = $id('store');
  for (const def of BUILDINGS) {
    store.appendChild(createBuildingRow(def));
  }

  document.querySelectorAll<HTMLButtonElement>('.bulk-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.bulk-btn').forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');
      state.bulkBuyAmount = parseInt(btn.dataset.amount ?? '1', 10);
    });
  });
}

function onCookieClick(e: MouseEvent): void {
  const value = clickSystem.click(state);
  if (state.prefs.numbers) {
    spawnClickParticle(value.toNumber());
  }
  if (state.prefs.particles) {
    const cookie = $id('bigCookie');
    const rect = cookie.getBoundingClientRect();
    particleSystem.spawn(
      e.clientX - rect.left + rect.width * 0.1,
      e.clientY - rect.top,
      8,
    );
  }
}

function createBuildingRow(def: (typeof BUILDINGS)[number]): HTMLElement {
  const row = document.createElement('div');
  row.id = `building-${def.id}`;
  row.className = 'building-row';
  row.innerHTML = `
    <div class="building-icon">${def.icon}</div>
    <div class="building-info">
      <div class="building-name">${def.name}</div>
      <div class="building-price-line">
        <span class="building-price">0</span> 🍪
      </div>
    </div>
    <div class="building-amount">0</div>
  `;

  tooltipSystem.attach(row, () => {
    const bs = state.buildings.get(def.id);
    const owned = bs?.amount ?? 0;
    const price = buildingPrice(bn(def.basePrice), def.priceMultiplier, owned);
    const canAfford = state.cookies.gte(price);
    const priceColor = canAfford ? '#6fbf73' : '#f44336';
    const buildingCps = cpsCalc.getBuildingCps(state, def.id);
    const totalCps = state.cookiesPs;
    const pct =
      totalCps.gt(0)
        ? Math.round(buildingCps.div(totalCps).mul(100).toNumber())
        : null;
    const pctLine =
      pct != null
        ? `<div style="font-size:13px;margin-bottom:4px;color:var(--text-dim)">${pct}% of total CpS</div>`
        : '';
    return `
      <div style="min-width:220px">
        <div style="font-size:16px;font-weight:700;margin-bottom:4px">${def.icon} ${def.name}</div>
        <div style="font-size:13px;color:#ccc;margin-bottom:10px">${def.desc}</div>
        <div style="font-size:13px;margin-bottom:4px">
          Each produces <strong style="color:var(--gold)">${beautify(bn(def.baseCps))}</strong> CpS
        </div>
        <div style="font-size:13px;margin-bottom:4px">
          ${owned} owned — producing <strong style="color:var(--gold)">${beautify(buildingCps)}</strong> CpS total
        </div>
        ${pctLine}
        <div style="margin-top:8px;font-size:14px;color:${priceColor}">
          Cost: <strong>${beautify(price)}</strong> cookies
        </div>
      </div>
    `;
  });

  row.addEventListener('click', () => {
    buildingSystem.buy(state, def.id, state.bulkBuyAmount);
  });
  return row;
}

function spawnClickParticle(value: number): void {
  const container = document.getElementById('particle-container');
  if (!container) return;
  const el = document.createElement('div');
  el.className = 'click-particle';
  el.textContent = `+${Math.round(value)}`;
  const offsetX = (Math.random() - 0.5) * 100;
  el.style.left = `calc(50% + ${offsetX}px)`;
  el.style.top = '40%';
  container.appendChild(el);
  el.addEventListener('animationend', () => el.remove());
}

function getPlayFunCookiesDelta(): number {
  const rawDelta = state.cookiesEarned.sub(lastPlayFunCookiesEarned);
  if (rawDelta.lte(0)) return 0;
  const clamped = rawDelta.gt(PLAYFUN_MAX_COOKIES_DELTA) ? PLAYFUN_MAX_COOKIES_DELTA : rawDelta.toNumber();
  return Math.max(0, Math.floor(clamped));
}

async function submitPlayFunEndRound(deltaOverride?: number): Promise<void> {
  if (!playFunIntegration.isEnabled() || !playFunIntegration.isReady() || playFunSubmitInFlight) return;
  const cookiesDelta = Math.max(0, Math.floor(deltaOverride ?? getPlayFunCookiesDelta()));
  if (cookiesDelta <= 0) return;
  const rawCps = state.cookiesPs.toNumber();
  const cookiesPs = Number.isFinite(rawCps) ? Math.max(0, Math.min(rawCps, PLAYFUN_MAX_CPS_INPUT)) : PLAYFUN_MAX_CPS_INPUT;

  playFunSubmitInFlight = true;
  try {
    const result = await playFunIntegration.submitEndRound(cookiesDelta, cookiesPs);
    lastPlayFunSubmitResult = result;
    if ((result as { ok?: boolean }).ok) {
      lastPlayFunCookiesEarned = state.cookiesEarned;
      if (import.meta.env.DEV) {
        console.info('[PlayFun] end-round submitted', result);
      }
    } else if (import.meta.env.DEV) {
      console.warn('[PlayFun] end-round skipped/failed', result);
    }
  } finally {
    playFunSubmitInFlight = false;
  }
}

if (import.meta.env.DEV) {
  (window as any).cheat = {
    cookies: (n = 1e12) => { state.cookies = state.cookies.add(n); state.cookiesEarned = state.cookiesEarned.add(n); },
    buff: (typeId = 'frenzy', duration?: number) => {
      const def = buffSystem.getTypeDef(typeId);
      if (!def) {
        console.warn(`Unknown buff "${typeId}". Available:`, BUFF_TYPES.map((b) => b.id));
        return;
      }
      const seconds = duration ?? 30;
      buffSystem.gain(state, typeId, seconds);
      console.log(`Buff "${typeId}" applied for ${seconds}s`);
    },
    buffs: () => console.table(BUFF_TYPES.map((b) => ({ id: b.id, name: b.name, desc: b.desc }))),
    star: () => shimmerSystem.debugSpawnGolden(state),
    golden: (effect: 'random' | 'frenzy' | 'click_frenzy' | 'lucky' | 'clot' = 'random') =>
      shimmerSystem.debugTriggerGolden(effect),
    playfun: () => console.log({
      enabled: playFunIntegration.isEnabled(),
      ready: playFunIntegration.isReady(),
      playerId: playFunIntegration.getPlayerId(),
      hasSessionToken: Boolean(playFunIntegration.getSessionToken()),
    }),
    playfunLogin: async () => playFunIntegration.login(),
    playfunRefresh: async () => playFunIntegration.refreshPointsAndMultiplier(),
    playfunSubmit: async (cookiesDelta?: number) => submitPlayFunEndRound(cookiesDelta),
    playfunStatus: () => console.log({
      enabled: playFunIntegration.isEnabled(),
      ready: playFunIntegration.isReady(),
      playerId: playFunIntegration.getPlayerId(),
      hasSessionToken: Boolean(playFunIntegration.getSessionToken()),
      inFlight: playFunSubmitInFlight,
      lastResult: lastPlayFunSubmitResult,
      pendingCookiesDelta: getPlayFunCookiesDelta(),
    }),
    cps: () => console.log(state.cookiesPs.toString()),
    state: () => console.log(state),
  };
}

document.addEventListener('DOMContentLoaded', () => void init());
